// Copyright (c) 2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from 'react';
import './PageBreakBuilder.scss';
import Button from "../../../../nor/ui/components/button/Button";
import Icon from "../../../../nor/ui/components/icon/Icon";
import {AddIcon, TrashIcon} from "../../../../assets/icon";
import TextField from "../../../../nor/ui/components/fields/text/TextField";
import {ChangeCallback, VoidCallback} from "../../../../nor/ts/interfaces/callbacks";
import PageBreakModel from "../../../../nor/ui/types/items/PageBreakModel";
import {FormBuilderState} from "../form/FormBuilder";
import Popup from "../../../../nor/ui/components/popup/Popup";
import FormAppClassName from "../../../../constants/FormAppClassName";

export interface PageBreakBuilderState {
    actionsMenuOpen  : boolean;
}

export interface PageBreakBuilderProps {

    model     : PageBreakModel;
    onChange ?: ChangeCallback<PageBreakModel>;
    onDelete ?: VoidCallback;

}

export class PageBreakBuilder extends React.Component<PageBreakBuilderProps, PageBreakBuilderState> {

    private readonly _onDeleteModelCallback : VoidCallback;

    private readonly _onChangeBackLabelCallback : ChangeCallback<string|undefined>;
    private readonly _onResetBackLabelCallback  : VoidCallback;
    private readonly _onDeleteBackLabelCallback : VoidCallback;

    private readonly _onChangeNextLabelCallback : ChangeCallback<string|undefined>;
    private readonly _onResetNextLabelCallback  : VoidCallback;
    private readonly _onDeleteNextLabelCallback : VoidCallback;

    private readonly _toggleActionsMenuCallback : VoidCallback;

    constructor(props: PageBreakBuilderProps) {

        super(props);

        this.state = {
            actionsMenuOpen: false
        };

        this._onDeleteModelCallback = this._onDeleteModel.bind(this);

        this._onChangeBackLabelCallback = this._onChangeBackLabel.bind(this);
        this._onResetBackLabelCallback  = this._onResetBackLabel.bind(this);
        this._onDeleteBackLabelCallback = this._onDeleteBackLabel.bind(this);

        this._onChangeNextLabelCallback = this._onChangeNextLabel.bind(this);
        this._onResetNextLabelCallback  = this._onResetNextLabel.bind(this);
        this._onDeleteNextLabelCallback = this._onDeleteNextLabel.bind(this);

        this._toggleActionsMenuCallback = this._onToggleActionsMenu.bind(this);

    }

    private _changeModel (value : PageBreakModel) {

        if (this.props.onChange) {
            try {
                this.props.onChange(value);
            } catch (err) {
                console.error('Error while calling onChange prop: ', err);
            }
        } else {
            console.warn('Parent did not define onChange prop');
        }

    }

    private _onDeleteModel () {

        if (this.props.onDelete) {
            try {
                this.props.onDelete();
            } catch (err) {
                console.error('Error while calling onDelete prop: ', err);
            }
        } else {
            console.warn('Parent did not define onDelete prop');
        }

    }


    private _onChangeBackLabel (value: string | undefined) {

        this._changeModel({
            ...this.props.model,
            backLabel: value ?? ''
        });

    }

    private _onResetBackLabel () {
        this._onChangeBackLabel('');
        this._closeActionsMenu();
    }

    private _onDeleteBackLabel () {

        const {backLabel, ...newModel} = this.props.model;

        this._changeModel(newModel);

    }


    private _onChangeNextLabel (value: string | undefined) {

        this._changeModel({
            ...this.props.model,
            nextLabel: value ?? ''
        });

    }

    private _onResetNextLabel () {
        this._onChangeNextLabel('');
        this._closeActionsMenu();
    }

    private _onDeleteNextLabel () {

        const {nextLabel, ...newModel} = this.props.model;
        this._changeModel(newModel);

    }


    render () {

        const item        : PageBreakModel     = this.props.model;
        const backLabel   : string | undefined = item?.backLabel;
        const nextLabel   : string | undefined = item?.nextLabel;

        return (
            <div className={FormAppClassName.FIELD_BUILDER}>

                <header>

                    <h4>Page break</h4>

                    <Button
                        click={this._toggleActionsMenuCallback}
                        className={
                            FormAppClassName.FORM_BUILDER+ '-action-menu-button '
                            + FormAppClassName.FORM_BUILDER + (this.state.actionsMenuOpen ? '-action-menu-open' : '-action-menu-closed')
                        }
                    ><Icon><AddIcon /></Icon></Button>

                    <Popup open={this.state.actionsMenuOpen}>

                        {nextLabel !== undefined ? null : (
                            <Button click={this._onResetNextLabelCallback}><Icon><AddIcon /></Icon> Next button label</Button>
                        )}

                        {backLabel !== undefined ? null : (
                            <Button click={this._onResetBackLabelCallback}><Icon><AddIcon /></Icon> Back button label</Button>
                        )}

                    </Popup>

                    <Button click={this._onDeleteModelCallback}><Icon><TrashIcon /></Icon></Button>

                </header>

                {nextLabel !== undefined ? (
                    <div className={FormAppClassName.FORM_VIEW + '-content-page-row'}>

                        <TextField
                            label='Next button'
                            placeholder={'Label'}
                            value={nextLabel}
                            change={this._onChangeNextLabelCallback}
                        >
                            <Button click={this._onDeleteNextLabelCallback}><Icon><TrashIcon /></Icon></Button>
                        </TextField>

                    </div>
                ) : null}

                {backLabel !== undefined ? (
                    <div className={FormAppClassName.FORM_VIEW + '-content-page-row'}>

                        <TextField
                            label='Back button'
                            placeholder={'Label'}
                            value={backLabel}
                            change={this._onChangeBackLabelCallback}
                        >
                            <Button click={this._onDeleteBackLabelCallback}><Icon><TrashIcon /></Icon></Button>
                        </TextField>

                    </div>
                ) : null}

            </div>
        );
    }

    private _onToggleActionsMenu () {

        this.setState((state : FormBuilderState) => ({actionsMenuOpen: !state.actionsMenuOpen}));

    }

    private _closeActionsMenu () {

        this.setState({actionsMenuOpen: false});

    }

}

export default PageBreakBuilder;
